import { html, nothing } from "lit";
import {
  type SystemOverviewData,
  type SystemOverviewSubView,
  SYSTEM_OVERVIEW_SUBVIEWS,
} from "../controllers/system-overview.ts";

export type SystemOverviewProps = {
  loading: boolean;
  data: SystemOverviewData | null;
  error: string | null;
  subView: SystemOverviewSubView;
  onSubViewChange: (view: SystemOverviewSubView) => void;
  onRefresh: () => void;
};

export function renderSystemOverview(props: SystemOverviewProps) {
  return html`
    <div class="card" style="overflow-x:auto">
      <div class="row" style="justify-content: space-between; align-items: center;">
        <div>
          <h3>System Overview</h3>
          <p class="muted" style="margin-bottom:0.5rem">
            Live tools, plugins, and config schema from the running gateway.
          </p>
        </div>
        <div class="row" style="gap: 8px;">
          <select
            class="btn"
            style="font-size:0.85em;padding:4px 8px;border-radius:6px;"
            .value=${props.subView}
            @change=${(e: Event) =>
              props.onSubViewChange(
                (e.target as HTMLSelectElement).value as SystemOverviewSubView,
              )}
          >
            ${SYSTEM_OVERVIEW_SUBVIEWS.map(
              (v) => html`<option value=${v.value} ?selected=${props.subView === v.value}>
                ${v.label}
              </option>`,
            )}
          </select>
          <button class="btn" ?disabled=${props.loading} @click=${props.onRefresh}>
            ${props.loading ? "Loading…" : "Refresh"}
          </button>
        </div>
      </div>

      ${props.error
        ? html`<div class="callout danger" style="margin-top: 12px;">${props.error}</div>`
        : nothing}

      ${!props.data && !props.loading
        ? html`<div class="muted" style="margin-top: 12px;">Click Refresh to load data.</div>`
        : nothing}

      ${props.data
        ? html`
            <div style="margin-top: 16px;">
              ${props.subView === "tools-system"
                ? renderToolsSystem(props.data)
                : nothing}
              ${props.subView === "tools-config"
                ? renderToolsConfig(props.data)
                : nothing}
              ${props.subView === "plugins-system"
                ? renderPluginsSystem(props.data)
                : nothing}
              ${props.subView === "plugins-config"
                ? renderPluginsConfig(props.data)
                : nothing}
              ${props.subView === "config-schema"
                ? renderConfigSchema(props.data)
                : nothing}
            </div>
          `
        : nothing}
    </div>
  `;
}

function renderToolsSystem(data: SystemOverviewData) {
  const tools = data.tools.filter((t) => !t.optional);
  return html`
    <p class="muted" style="margin-bottom:0.75rem">
      Built-in agent tools that work out of the box. Always available (subject to tool policy profiles).
    </p>
    <table class="settings-table">
      <thead>
        <tr>
          <th>#</th>
          <th>Tool Name</th>
          <th>Plugin</th>
          <th>Source</th>
        </tr>
      </thead>
      <tbody>
        ${tools.length === 0
          ? html`<tr><td colspan="4" class="muted">No built-in tools loaded.</td></tr>`
          : tools.flatMap((t, i) =>
              t.names.map((name) => html`
                <tr>
                  <td>${i + 1}</td>
                  <td><code>${name}</code></td>
                  <td class="muted" style="font-size:0.85em">${t.pluginId}</td>
                  <td class="muted" style="font-size:0.85em">${t.source}</td>
                </tr>
              `),
            )}
      </tbody>
    </table>
    <p class="muted" style="margin-top:0.75rem">Total: ${tools.length} tool registrations</p>
  `;
}

function renderToolsConfig(data: SystemOverviewData) {
  const tools = data.tools.filter((t) => t.optional);
  return html`
    <p class="muted" style="margin-bottom:0.75rem">
      Agent tools that need API keys, database URLs, or external services to function.
    </p>
    <table class="settings-table">
      <thead>
        <tr>
          <th>#</th>
          <th>Tool Name</th>
          <th>Plugin</th>
          <th>Source</th>
        </tr>
      </thead>
      <tbody>
        ${tools.length === 0
          ? html`<tr><td colspan="4" class="muted">No config-required tools loaded.</td></tr>`
          : tools.flatMap((t, i) =>
              t.names.map((name) => html`
                <tr>
                  <td>${i + 1}</td>
                  <td><code>${name}</code></td>
                  <td class="muted" style="font-size:0.85em">${t.pluginId}</td>
                  <td class="muted" style="font-size:0.85em">${t.source}</td>
                </tr>
              `),
            )}
      </tbody>
    </table>
    <p class="muted" style="margin-top:0.75rem">Total: ${tools.length} tool registrations</p>
  `;
}

function renderPluginsSystem(data: SystemOverviewData) {
  const plugins = data.plugins.filter((p) => p.origin === "bundled");
  return html`
    <p class="muted" style="margin-bottom:0.75rem">
      Bundled plugins that work out of the box once the platform is running. No API keys or external services required.
    </p>
    <table class="settings-table">
      <thead>
        <tr>
          <th>#</th>
          <th>Plugin ID</th>
          <th>Name</th>
          <th>Status</th>
          <th>Tools</th>
          <th>Channels</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        ${plugins.length === 0
          ? html`<tr><td colspan="7" class="muted">No bundled plugins loaded.</td></tr>`
          : plugins.map((p, i) => html`
              <tr>
                <td>${i + 1}</td>
                <td><code>${p.id}</code></td>
                <td>${p.name}</td>
                <td>
                  <span class="chip ${p.status === "loaded" ? "chip--ok" : "chip--muted"}">
                    ${p.status}
                  </span>
                </td>
                <td class="muted" style="font-size:0.85em">${p.toolNames.length > 0 ? p.toolNames.join(", ") : "—"}</td>
                <td class="muted" style="font-size:0.85em">${p.channelIds.join(", ") || "—"}</td>
                <td class="muted" style="font-size:0.85em">${p.description ?? "—"}</td>
              </tr>
            `)}
      </tbody>
    </table>
    <p class="muted" style="margin-top:0.75rem">Total: ${plugins.length} plugins</p>
  `;
}

function renderPluginsConfig(data: SystemOverviewData) {
  const plugins = data.plugins.filter((p) => p.origin !== "bundled");
  return html`
    <p class="muted" style="margin-bottom:0.75rem">
      Plugins from workspace, global, or config sources that may need database URLs, API keys, or external services.
    </p>
    <table class="settings-table">
      <thead>
        <tr>
          <th>#</th>
          <th>Plugin ID</th>
          <th>Name</th>
          <th>Origin</th>
          <th>Status</th>
          <th>Enabled</th>
          <th>Tools</th>
          <th>Channels</th>
          <th>Description</th>
          ${plugins.some((p) => p.error)
            ? html`<th>Error</th>`
            : nothing}
        </tr>
      </thead>
      <tbody>
        ${plugins.length === 0
          ? html`<tr><td colspan="9" class="muted">No config-required plugins found.</td></tr>`
          : plugins.map((p, i) => html`
              <tr>
                <td>${i + 1}</td>
                <td><code>${p.id}</code></td>
                <td>${p.name}</td>
                <td class="muted" style="font-size:0.85em">${p.origin}</td>
                <td>
                  <span class="chip ${p.status === "loaded" ? "chip--ok" : p.status === "error" ? "chip--danger" : "chip--muted"}">
                    ${p.status}
                  </span>
                </td>
                <td>${p.enabled ? "Yes" : "No"}</td>
                <td class="muted" style="font-size:0.85em">${p.toolNames.length > 0 ? p.toolNames.join(", ") : "—"}</td>
                <td class="muted" style="font-size:0.85em">${p.channelIds.join(", ") || "—"}</td>
                <td class="muted" style="font-size:0.85em">${p.description ?? "—"}</td>
                ${plugins.some((p) => p.error)
                  ? html`<td class="muted" style="font-size:0.8em">${p.error ?? "—"}</td>`
                  : nothing}
              </tr>
            `)}
      </tbody>
    </table>
    <p class="muted" style="margin-top:0.75rem">Total: ${plugins.length} plugins</p>
  `;
}

function renderConfigSchema(data: SystemOverviewData) {
  const sections = data.configSections;
  return html`
    <p class="muted" style="margin-bottom:0.75rem">
      All configuration sections available in the system, derived from the live config schema.
    </p>
    <table class="settings-table">
      <thead>
        <tr>
          <th>#</th>
          <th>Section</th>
          <th>Title</th>
          <th>Description</th>
        </tr>
      </thead>
      <tbody>
        ${sections.length === 0
          ? html`<tr><td colspan="4" class="muted">No config sections found.</td></tr>`
          : sections.map((s, i) => html`
              <tr>
                <td>${i + 1}</td>
                <td><code>${s.section}</code></td>
                <td>${s.title ?? "—"}</td>
                <td class="muted" style="font-size:0.85em">${s.description ?? "—"}</td>
              </tr>
            `)}
      </tbody>
    </table>
    <p class="muted" style="margin-top:0.75rem">Total: ${sections.length} config sections</p>
  `;
}
